import { FC } from 'react'
import { Box, findBy, H2, IconButton, PageTitle } from '@perk-ui/core'
import { ArrowLeft } from 'react-feather'
import { useLocation, useParams } from 'react-router'

import { useSitesContext } from '../../components/SitesProvider'
import { surveyPeriodsOrdered, TIME_ZONES } from '../../config/constants'
import useBodySites from '../../features/query-hooks/useBodySites'
import useSurveyResponseSearch from '../../features/query-hooks/useSurveyResponseSearch'
import { useHistory } from '../../utils/useHistory'
import SurveyTable from './components/SurveyTable'

export interface PatientCaseSurveysProps {}

const PatientCaseSurveys: FC<PatientCaseSurveysProps> = () => {
  const { push, go } = useHistory()
  const location = useLocation<{ from?: string }>()
  const { caseId, patientId } = useParams<{
    patientId: string
    caseId: string
  }>()

  const { data: allSurveys } = useSurveyResponseSearch({
    ownerIds: [caseId],
    ownerType: 'PatientCase',
    includes: ['survey_response_scores'],
    perPage: '300',
  })
  const { currentSite } = useSitesContext()
  const siteTimeZone =
    TIME_ZONES.find((tz) => tz.rails === currentSite?.timeZone)?.client || 'UTC'
  const bodySiteSlug = allSurveys?.surveyResponses[0]?.bodySiteSlug
  const { data: bodySites = [] } = useBodySites()
  const bodySite = findBy('slug', bodySiteSlug, bodySites)

  const surveysGroupedBySchedule = allSurveys?.surveyResponses?.reduce(
    (acc, item) => {
      if (Object.keys(item?.metadata).length === 0) return acc

      const itemSchedule = findBy('type', 'schedule', item.metadata)

      if (itemSchedule && item?.survey?.slug !== 'TREATMENT-HISTORY') {
        acc[itemSchedule.value] = acc[itemSchedule.value] || []
        acc[itemSchedule.value].push(item)
      }
      return acc
    },
    Object.create(null),
  )

  const surveyPeriods = surveysGroupedBySchedule
    ? surveyPeriodsOrdered
        .sort((a, b) => a.order - b.order)
        .filter((period) => {
          return Object.keys(surveysGroupedBySchedule).find(
            (sgbs) => period.slug === sgbs,
          )
        })
        .map((per) => per.slug)
    : []

  Object.keys(surveysGroupedBySchedule || {})
    .sort()
    .map((sgbs) => {
      if (sgbs.includes('followup_')) {
        surveyPeriods.push(sgbs)
      }
    })

  return (
    <Box p={3}>
      <PageTitle title="Patient" />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
        }}
      >
        <H2 sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => {
              if (location.state?.from) {
                if (location.state?.from === '/patients') {
                  go(-1)
                } else {
                  push(location.state.from)
                }
              } else {
                push(`/patients/${patientId}`)
              }
            }}
            sx={{ mr: 1 }}
            size="large"
          >
            <ArrowLeft />
          </IconButton>
          <span>{bodySite?.name || '...'} Survey Scores & Schedule</span>
        </H2>
      </Box>
      <SurveyTable
        surveys={allSurveys}
        surveyPeriods={surveyPeriods}
        timezone={siteTimeZone}
      />
    </Box>
  )
}

export default PatientCaseSurveys
